import { LitElement, html } from 'lit-element';
import translate from '../../utils/webcomponents/translate';
import { subscribeToWebinar } from './webinar-registration-api';
import { updatePageAfterRegistration } from './webinar-registration-storage';

class WebinarRegistrationForm extends LitElement {
	static get properties() {
		return {
			options: { type: Object },
			isError: { type: Boolean },
			isSuccess: { type: Boolean },
		};
	}

	constructor() {
		super();

		this.isError = false;
		this.isSuccess = false;
	}

	renderHighlightedHeader = ({
		webinarTitle,
		dateDisplay,
		dateDatetime,
		startTimeDisplay,
		startTimeDatetime,
		endTimeDisplay,
		endTimeDatetime,
	}) => {
		const webinarLabel = translate('webinarcontent', 'webinar', 'Webinar');
		const dateLabel = translate('webinarcontent', 'date', 'Date');
		const startTimeLabel = translate('webinarcontent', 'startTime', 'Start time');

		return html`
			<dl
				class="section--compact section--highlighted display-grid grid-cols-max-1fr align-center gap-column--medium"
			>
				<dt class="text-uppercase text-bold">${webinarLabel}:</dt>
				<dd>${webinarTitle}</dd>
				<dt class="text-uppercase text-bold">${dateLabel}:</dt>
				<dd>
					<time datetime="${dateDatetime}">${dateDisplay}</time>
				</dd>
				<dt class="text-uppercase text-bold">${startTimeLabel}:</dt>
				<dd>
					${startTimeDisplay ? html`<time datetime="${startTimeDatetime}">${startTimeDisplay}</time>` : ''}
					${startTimeDisplay && endTimeDisplay ? html`&mdash;` : ''}
					${endTimeDisplay ? html`<time datetime="${endTimeDatetime}">${endTimeDisplay}</time>` : ''}
				</dd>
			</dl>
		`;
	};

	renderRegistrationForm = ({ firstName = '', lastName = '', email = '', showPersonalDetailsFields }) => {
		const labelFirstName = translate('webinarcontent', 'firstName', 'First Name');
		const labelLastName = translate('webinarcontent', 'lastName', 'Last name');
		const labelEmail = translate('webinarcontent', 'email', 'Email');
		const labelCancel = translate('webinarcontent', 'cancel', 'Cancel');
		const labelRegister = translate('webinarcontent', 'register', 'Register');

		return html`
			<form
				class="form trailer-reset"
				@submit=${this.onSubmit}
			>
				${showPersonalDetailsFields
					? html`<div class="field__group field__group--grid gap--small gap-column--medium">
							<div class="field">
								<label
									class="field__label required"
									for="webinar-firstName"
									>${labelFirstName}</label
								>
								<input
									type="text"
									class="field__input"
									name="firstName"
									id="webinar-firstName"
									value="${firstName}"
									autocomplete="given-name"
									required
									aria-required="true"
								/>
							</div>

							<div class="field">
								<label
									class="field__label required"
									for="webinar-lastName"
									>${labelLastName}</label
								>
								<input
									type="text"
									class="field__input"
									name="lastName"
									id="webinar-lastName"
									value="${lastName}"
									autocomplete="family-name"
									required
									aria-required="true"
								/>
							</div>
							<div class="field field--full">
								<label
									class="field__label required"
									for="webinar-email"
									>${labelEmail}</label
								>
								<input
									type="email"
									class="field__input"
									name="email"
									id="webinar-email"
									value="${email}"
									autocomplete="email"
									required
									aria-required="true"
								/>
							</div>
						</div>`
					: ``}

				<div class="form__footer form__footer--end gap--medium">
					<button
						type="button"
						class="button button--ghost js-close"
					>
						${labelCancel}
					</button>
					<button
						type="submit"
						class="button"
					>
						${labelRegister}
					</button>
				</div>
			</form>
		`;
	};

	renderUserMessage = () => {
		const { userEmail, loginUrl, showPersonalDetailsFields } = this.options;

		if (!showPersonalDetailsFields) {
			const loggedInPrefix = translate(
				'webinarcontent',
				'registrationLoggedInMessagePrefix',
				'You are logged in as',
			);
			const loggedInSuffix = translate(
				'webinarcontent',
				'registrationLoggedInMessageSuffix',
				'your registration will be linked to this.',
			);

			const email = html`<span class="text-bold">${userEmail}</span>`;

			return html`<p>${loggedInPrefix} ${email}. ${loggedInSuffix}</p>`;
		}

		if (loginUrl) {
			const registrationLoginPrompt = translate(
				'webinarcontent',
				'registrationLoginPrompt',
				'Please enter your details to register for the webinar or',
			);
			const loginLinkText = translate('webinarcontent', 'registrationLoginLinkLabel', 'log in to your account');

			return html`
				<p>
					${registrationLoginPrompt}&nbsp;
					<a href="${loginUrl}">${loginLinkText}</a>.
				</p>
			`;
		}

		return null;
	};

	onSubmit = (e) => {
		e.preventDefault();

		this.registerForWebinar(e.target);
	};

	getStringValue = (val) => (typeof val === 'string' ? val.trim() : '');

	async registerForWebinar(form) {
		const { options, getStringValue } = this;
		const { slug, culture, showPersonalDetailsFields } = options;

		const formData = new FormData(form);

		const firstName = getStringValue(formData.get('firstName'));
		const lastName = getStringValue(formData.get('lastName'));
		const email = getStringValue(formData.get('email'));

		this.isSuccess = false;
		this.isError = false;

		if (!slug || !culture) {
			this.isError = true;
			return;
		}

		try {
			const result = await subscribeToWebinar(showPersonalDetailsFields, {
				slug,
				culture,
				email,
				firstName,
				lastName,
			});

			if (result?.isSubscriptionSuccessful) {
				this.isSuccess = true;

				const videoEmbedCode = result?.videoEmbedCode || '';
				updatePageAfterRegistration(slug, videoEmbedCode, options.status);
				return;
			}

			this.isError = true;
		} catch {
			this.isError = true;
		}
	}

	renderStatusMessage = () => {
		if (this.isSuccess) {
			const displayMessage = translate(
				'webinarcontent',
				'subscriptionSuccessMessage',
				'You have successfully registered for the webinar.',
			);
			const labelClose = translate('webinarcontent', 'close', 'Close');

			return html`
				<x-message
					modifier="positive"
					icon="checkmark"
					.message=${displayMessage}
				></x-message>
				<div class="form__footer form__footer--centered">
					<button
						type="button"
						class="button js-close"
					>
						${labelClose}
					</button>
				</div>
			`;
		}

		if (this.isError) {
			const errorMessage = translate(
				'webinarcontent',
				'subscriptionFailureMessage',
				'Webinar registration failed. Please try again later',
			);

			return html`
				<x-message
					modifier="error"
					icon="warning"
					.message=${errorMessage}
				></x-message>
			`;
		}

		return null;
	};

	createRenderRoot() {
		return this;
	}

	render() {
		const { options, isSuccess } = this;

		const highlightedHeader = this.renderHighlightedHeader(options);
		const renderUserMessage = this.renderUserMessage();
		const registrationForm = html`${renderUserMessage} ${this.renderRegistrationForm(options)}`;
		const statusMessage = this.renderStatusMessage();

		return html` <div>${highlightedHeader} ${statusMessage} ${isSuccess ? '' : registrationForm}</div> `;
	}
}

customElements.define('x-webinar-registration-form', WebinarRegistrationForm);

export default WebinarRegistrationForm;
